import { DrawingTypes } from '@/types';
import { ShapeTypes } from '@/types/shapes';
import { RGBColor } from 'react-color';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'pt', 'tr', 'fr', 'ru'];

export enum PROFILE_ROUTES {
  general = 'general',
  cards = 'cards',
  subscriptions = 'subscriptions',
  payments = 'payments',
}

export const ROUTES = {
  home: (lang: string) => `/${lang}`,
  restorePassword: (lang: string) => `/${lang}/restore-password/:code`,
  edit: (lang: string, documentId: string) => `/${lang}/edit/${documentId}`,
  editNew: (lang: string) => `/${lang}/edit/new`,
  results: (lang: string, documentId: string) => `/${lang}/results/${documentId}`,
  myFiles: (lang: string) => `/${lang}/my-documents`,
  pricing: (lang: string) => `/${lang}/pricing`,
  refund: (lang: string) => `/${lang}/refund-policy`,
  policy: (lang: string) => `/${lang}/privacy-policy`,
  gdpr: (lang: string) => `/${lang}/gdpr`,
  terms: (lang: string) => `/${lang}/terms-of-service`,
  workInProgress: (lang: string) => `/${lang}/work-in-progress`,
  subscribed: (lang: string) => `/${lang}/subscribed`,
  subscribedDodo: (lang: string, tariff: string, documentId: string) => `/${lang}/dodoResult/${tariff}/${documentId}`,
  convertToWord: (lang: string) => `/${lang}/convert/pdf-to-word`,
  profile: (lang: string, subRoute: PROFILE_ROUTES | '*') => `/${lang}/profile/${subRoute}`,
};

/* -------------------------------------- Signatures -------------------------------------- */
export const SIGNATURE_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717', '#FFFFFF'];

export const SIGNATURE_DRAWING_AREA_WIDTH = 500;
export const SIGNATURE_DRAWING_AREA_HEIGHT = 300;

export const SIGNATURE_TYPING_FONTS = [
  'Over The Rainbow',
  'Stalemate',
  'Caveat',
  'Cedarville Cursive',
  'Dancing Script',
];

/* -------------------------------------- Annotations -------------------------------------- */

export const ANNOTATION_DRAWING_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717'];

export const ANNOTATION_LINE_WIDTHS = {
  [DrawingTypes.Draw]: 7,
  [DrawingTypes.Highlight]: 20,
};

/** Selectable stroke widths (px) for annotations — shared by the Draw picker and the post-draw toolbar. */
export const ANNOTATION_MIN_WIDTH = 1;
export const ANNOTATION_MAX_WIDTH = 32;
export const ANNOTATION_WIDTH_OPTIONS = Array.from({ length: ANNOTATION_MAX_WIDTH }, (_, i) => ({
  label: String(i + 1),
  value: i + 1,
}));

export const DUPLICATE_SHAPE_OFFSET = 10;

/* -------------------------------------- Shapes -------------------------------------- */

// Palette offered by the Shapes picker (shared with annotations for visual consistency).
export const SHAPE_DRAWING_COLORS = ['#001F3F', '#083358', '#0D63A5', '#FFD717'];

// Selectable border/line width (px) range for the Shapes picker slider.
export const SHAPE_MIN_WIDTH = 1;
export const SHAPE_MAX_WIDTH = 32;

// Per-shape default border/line width. Outline shapes are thinner; lines/arrows read heavier.
// Matches the fallbacks applied in shapesReducer's ADD_SHAPE.
export const SHAPE_BORDER_WIDTHS: Record<ShapeTypes, number> = {
  [ShapeTypes.Ellipse]: 2,
  [ShapeTypes.Rectangle]: 2,
  [ShapeTypes.Arrow]: 4,
  [ShapeTypes.Line]: 4,
};

/* -------------------------------------- Document Editor -------------------------------------- */

// TODO: ??? figure out why 2 - isn't it supposed to be calculated ("e.g. "fit page" scale on the start)?
export const DEFAULT_SCALE = 2;

// TODO: ??? maybe determine the scale dynamically - it can depend on the document and screen dimensions
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;

export const RESIZE_HANDLE_POSITIONS = {
  Rectangle: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'],
  Textbox: [
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
    'topCenter',
    'bottomCenter',
    'leftCenter',
    'rightCenter',
  ],
} as const;

export const RESIZE_HANDLE_STYLES = {
  Rectangle: {
    cursor: {
      topLeft: 'nw-resize',
      topRight: 'ne-resize',
      bottomLeft: 'sw-resize',
      bottomRight: 'se-resize',
    } as Record<string, string>,
  },
  Textbox: {
    cursor: {
      topLeft: 'nw-resize',
      topRight: 'ne-resize',
      bottomLeft: 'sw-resize',
      bottomRight: 'se-resize',
      topCenter: 'n-resize',
      bottomCenter: 's-resize',
      leftCenter: 'w-resize',
      rightCenter: 'e-resize',
    } as Record<string, string>,
  },
};

export const SHAPE_HANDLE_SIZE = 9;

export const MAX_UNDO_STACK_SIZE = 15;

export const WHITEOUT_TEXT_THRESHOLD = 0.5;

// Palette offered by the erase tool when auto-pick color is disabled. White first (the common case).
export const WHITEOUT_COLORS = ['#FFFFFF', '#000000', '#001F3F', '#0D63A5', '#FFD717'];

// localStorage flag: once set, the erase-tool warning dialog is never shown again.
export const WHITEOUT_WARNING_DISMISSED_KEY = 'whiteoutWarningDismissed';

export const SHAPES_DRAWING_POINTS_MIN_DISTANCE = 10;

export const RGB_COLORS: Record<string, RGBColor> = {
  BLACK: { r: 0, g: 0, b: 0, a: 1 },
  GRAY: { r: 128, g: 128, b: 128, a: 1 },
  RED: { r: 255, g: 0, b: 0, a: 1 },
  TRANSPARENT: { r: 0, g: 0, b: 0, a: 0 },
};

export const COORDINATES_PIXELS_EPSILON = 0.5;

export const PDF_TO_WORD_MAX_TOTAL_FILE_SIZE = 50;
